/**
 * Pure score clock and duration resolution.
 *
 * A score is resolved once into section deadlines. Runtime reads then project a position from one
 * relay-clock anchor; they never accumulate animation ticks, so a throttled tab catches up on its
 * next read. The relay receives the resolved form too, which keeps enforcement to a deadline and
 * grant-table lookup rather than teaching it score composition rules.
 */

const DEFAULT_TEMPO_BPM = 120;
const DEFAULT_BEATS_PER_BAR = 4;

function positive(value) {
  return Number.isFinite(value) && value > 0 ? Number(value) : 0;
}

function durationBasisMs(section, tempoBpm, beatsPerBar) {
  const ms = positive(section?.ms);
  if (ms > 0) return ms;
  const bars = positive(section?.bars);
  if (bars <= 0) return 0;
  return bars * beatsPerBar * (60_000 / tempoBpm);
}

/**
 * Resolve mixed fixed (`ms` / `bars`) and weighted sections into one deadline table.
 * Fixed bases are paid first; weights share what remains. A score with only fixed sections ends
 * when its last fixed section ends, even when the room length is longer.
 */
export function resolveScoreSections(
  sections,
  roomDurationMs,
  { tempoBpm = DEFAULT_TEMPO_BPM, beatsPerBar = DEFAULT_BEATS_PER_BAR } = {},
) {
  const source = Array.isArray(sections) ? sections : [];
  if (source.length === 0) return { sections: [], totalMs: 0, minimumMs: 0 };

  const bpm = positive(tempoBpm) || DEFAULT_TEMPO_BPM;
  const meter = positive(beatsPerBar) || DEFAULT_BEATS_PER_BAR;
  const bases = source.map((section) => durationBasisMs(section, bpm, meter));
  const weights = source.map((section, index) => bases[index] > 0 ? 0 : positive(section?.weight) || 1);
  const minimumMs = bases.reduce((sum, value) => sum + value, 0);
  const weightTotal = weights.reduce((sum, value) => sum + value, 0);
  const available = Math.max(0, positive(roomDurationMs) - minimumMs);

  let cursor = 0;
  const resolved = source.map((section, index) => {
    const durationMs = bases[index] || (weightTotal > 0 ? available * (weights[index] / weightTotal) : 0);
    const startMs = cursor;
    cursor += durationMs;
    return {
      id: typeof section?.id === 'string' ? section.id : String(index),
      name: typeof section?.name === 'string' ? section.name : '',
      startMs,
      endMs: cursor,
      durationMs,
      grants: section?.grants && typeof section.grants === 'object' ? section.grants : {},
    };
  });

  return { sections: resolved, totalMs: cursor, minimumMs };
}

/** A whole count at or above zero, or the fallback. */
/** The section a position falls in — the last one when the position sits at (or past) the end. */
function sectionIndexAt(sections, positionMs) {
  let sectionIndex = sections.length - 1;
  for (let index = 0; index < sections.length; index += 1) {
    if (positionMs < sections[index].endMs) {
      sectionIndex = index;
      break;
    }
  }
  return sectionIndex;
}

function counter(value, fallback = 0) {
  const number = Number(value);
  return Number.isInteger(number) && number >= 0 ? number : fallback;
}

/**
 * A safe full transport snapshot, suitable for storage or a wire message.
 *
 * The two hand fields ride here rather than in a channel of their own, which is what makes them
 * survive a late join for nothing: the transport is already in the room's join snapshot.
 *  - `drawIndex` — the hand's re-deal counter. The dice moves it; every client then re-derives the
 *    same new hand from it. Only ever incremented, and only by the relay's fold.
 *  - `freeSectionIndex` — the section the hand is FROZEN to while the score runs free, or null.
 */
export function normalizeScoreTransport(raw, fallbackRt = 0) {
  return {
    positionMs: Math.max(0, Number.isFinite(raw?.positionMs) ? Number(raw.positionMs) : 0),
    changedRt: Number.isFinite(raw?.changedRt) ? Number(raw.changedRt) : fallbackRt,
    running: raw?.running === true,
    free: raw?.free === true,
    drawIndex: counter(raw?.drawIndex),
    freeSectionIndex: Number.isInteger(raw?.freeSectionIndex) && raw.freeSectionIndex >= 0
      ? raw.freeSectionIndex
      : null,
  };
}

/** Derive the section and pointer from an anchor at relay time `rt`. */
export function deriveScoreState(runtime, rawTransport, rt) {
  const sections = Array.isArray(runtime?.sections) ? runtime.sections : [];
  const totalMs = positive(runtime?.totalMs);
  const transport = normalizeScoreTransport(rawTransport, Number.isFinite(rt) ? rt : 0);
  const nowRt = Number.isFinite(rt) ? rt : transport.changedRt;
  const projected = transport.positionMs + (transport.running ? Math.max(0, nowRt - transport.changedRt) : 0);
  const positionMs = Math.min(totalMs, Math.max(0, projected));
  const ended = sections.length === 0 || positionMs >= totalMs;

  const sectionIndex = sections.length > 0 ? sectionIndexAt(sections, positionMs) : -1;
  const section = sectionIndex >= 0 ? sections[sectionIndex] : null;
  // Which section the HAND is dealt for, which is not always the one that is playing. Free leaves
  // the clock running, so the section pointer goes on advancing underneath it; the hand must not
  // follow, or a section boundary re-deals the cards out from under whoever has just rolled. While
  // free is on, the hand stays on the section the toggle happened in and only the dice moves it.
  const frozen = transport.free && transport.freeSectionIndex !== null
    ? Math.min(transport.freeSectionIndex, Math.max(0, sections.length - 1))
    : null;
  return {
    positionMs,
    sectionIndex,
    sectionCount: sections.length,
    section,
    sectionElapsedMs: section ? Math.max(0, positionMs - section.startMs) : 0,
    sectionRemainingMs: section ? Math.max(0, section.endMs - positionMs) : 0,
    running: transport.running && !ended,
    free: transport.free,
    ended,
    handSectionIndex: frozen ?? sectionIndex,
    drawIndex: transport.drawIndex,
  };
}

/** Fold one ordered semantic nudge into a new full transport snapshot. */
export function reduceScoreTransport(runtime, rawTransport, action, rt) {
  const nowRt = Number.isFinite(rt) ? rt : 0;
  const current = normalizeScoreTransport(rawTransport, nowRt);
  const derived = deriveScoreState(runtime, current, nowRt);
  const sections = runtime?.sections ?? [];
  let positionMs = derived.positionMs;
  let running = derived.running;
  let free = derived.free;
  let drawIndex = current.drawIndex;
  let freeSectionIndex = current.freeSectionIndex;

  switch (action) {
    // Next/Previous RE-PIN the frozen section rather than carrying the old one forward.
    //
    // The freeze exists so an automatic boundary cannot re-deal the hand under someone who just
    // rolled the dice. An explicit Next is the opposite of automatic: somebody asked for another
    // section. Leaving the pin where it was left the transport bar highlighting the new section
    // while the rail still showed the previous one's hand, with no way to resync short of toggling
    // free off and on again.
    case 'next': {
      const target = sections[derived.sectionIndex + 1];
      positionMs = target ? target.startMs : runtime?.totalMs ?? positionMs;
      running = Boolean(target) && current.running;
      if (free) freeSectionIndex = target ? derived.sectionIndex + 1 : derived.sectionIndex;
      break;
    }
    case 'previous': {
      const previousIndex = Math.max(0, derived.sectionIndex - 1);
      const target = sections[previousIndex];
      positionMs = target?.startMs ?? 0;
      running = current.running && !derived.ended;
      if (free) freeSectionIndex = previousIndex;
      break;
    }
    // A seek that crosses a section boundary re-pins exactly like Next/Previous does — the relay's
    // fold re-pins on these too, and this reducer is the spec any client-side fold reuses, so the
    // two must not encode different semantics.
    case 'forward':
      positionMs = Math.min(runtime?.totalMs ?? positionMs, positionMs + 3000);
      running = current.running && positionMs < (runtime?.totalMs ?? 0);
      if (free) freeSectionIndex = sectionIndexAt(sections, positionMs);
      break;
    case 'rewind':
      positionMs = Math.max(0, positionMs - 3000);
      running = current.running;
      if (free) freeSectionIndex = sectionIndexAt(sections, positionMs);
      break;
    case 'pause':
      running = derived.ended ? false : !current.running;
      break;
    case 'free':
      free = !current.free;
      // Freeze the hand where the toggle happened; leaving free hands it back to the score.
      freeSectionIndex = free ? derived.sectionIndex : null;
      break;
    case 'draw':
      // The dice: a re-deal, and nothing else — position, running and free are all left alone.
      // Refused inside a timed section, where the score itself is what deals.
      if (!derived.free && !derived.ended) return current;
      drawIndex += 1;
      break;
    default:
      return current;
  }

  return { positionMs, changedRt: nowRt, running, free, drawIndex, freeSectionIndex };
}

/** Grants in force at this instant. `allGrants` is used only by the independent free toggle. */
export function scoreGrantsAt(runtime, transport, rt, allGrants = {}) {
  const state = deriveScoreState(runtime, transport, rt);
  return state.free ? allGrants : state.section?.grants ?? {};
}
